/**
 * Timesheet unlock — a temporary, admin-granted override of the period
 * lock (see `timesheet-settings.ts`). An unlock re-opens a scope (a
 * customer, an employee, both, or everyone) for a date range until its
 * `autoEndDate` passes, at which point the affected periods re-lock
 * automatically — no admin has to remember to lock them again.
 *
 * This module is the shared contract between the API (authoritative
 * enforcement, in `TimesheetService.assertDatesNotLocked`) and the web
 * client (which greys-in unlocked cells in the grid). It is dependency-free
 * — keep it that way so it imports cleanly into both the Bun API and the
 * browser bundle. All dates are ISO YYYY-MM-DD strings; comparisons rely on
 * the fact that zero-padded ISO dates sort lexicographically == chronologically.
 */

/**
 * The fields of an unlock that matter for "does this cover that cell".
 * A superset of this (with ids, timestamps, audit columns) is what the
 * API stores and returns; the matcher only needs these.
 *
 *   - `userId`  null ⇒ applies to every employee; otherwise that one owner.
 *   - `customerId` null ⇒ applies to every customer (incl. internal,
 *     customer-less time); otherwise only entries on that customer.
 *   - `[fromDate, toDate]` inclusive range of entry dates it re-opens.
 *   - `autoEndDate` the last date (in the OWNER's timezone) the unlock is
 *     active; on the day after, the scope re-locks.
 *   - `revokedAt` non-null ⇒ an admin ended it early; treated as inactive.
 */
export interface TimesheetUnlockScope {
  userId: string | null;
  customerId: string | null;
  fromDate: string;
  toDate: string;
  autoEndDate: string;
  revokedAt: string | null;
}

/**
 * Whether `unlock` is still in effect as of `asOf` (ISO YYYY-MM-DD). An
 * unlock is active until the end of its `autoEndDate`, so it is active while
 * `asOf <= autoEndDate`. A revoked unlock is never active.
 *
 * This is THE single definition of "active" for an unlock. Every consumer
 * routes through it: the API row-view `active` flag, the timesheet lock-gate
 * override, and the web grid hint. The management-list SQL filter
 * (`statusPredicate` in the repository) is the one place this can't be
 * called — it mirrors the same rule in SQL (`revoked_at IS NULL AND
 * auto_end_date >= asOf`) and must be kept in lockstep with this function.
 */
export function isUnlockActive(unlock: TimesheetUnlockScope, asOf: string): boolean {
  if (unlock.revokedAt !== null) return false;
  return asOf <= unlock.autoEndDate;
}

/**
 * Whether `unlock`'s scope covers a timesheet entry for `ownerUserId` on
 * `customerId` (null for internal, customer-less time) at `date`. This is
 * the scope/range match only — it does NOT check whether the unlock is
 * still active; pair it with `isUnlockActive` (see `findCoveringUnlock`).
 */
export function unlockCoversEntry(
  unlock: TimesheetUnlockScope,
  ownerUserId: string,
  customerId: string | null,
  date: string,
): boolean {
  if (unlock.userId !== null && unlock.userId !== ownerUserId) return false;
  if (unlock.customerId !== null && unlock.customerId !== customerId) return false;
  return unlock.fromDate <= date && date <= unlock.toDate;
}

/**
 * The first unlock in `unlocks` that is both active (as of `asOf`) and
 * covers the given (owner, customer, date) cell, or null when none does.
 * Overlapping unlocks are allowed (warn-but-allow on create), so "any
 * active covering unlock re-opens the cell" — the first match is enough.
 */
export function findCoveringUnlock<T extends TimesheetUnlockScope>(
  unlocks: readonly T[],
  ownerUserId: string,
  customerId: string | null,
  date: string,
  asOf: string,
): T | null {
  for (const u of unlocks) {
    if (isUnlockActive(u, asOf) && unlockCoversEntry(u, ownerUserId, customerId, date)) {
      return u;
    }
  }
  return null;
}
